import { Params, Router } from '@angular/router';
import { ICompanyForm } from 'app/back-office/shared/models/back-office-admin-common/customer-check-in.model';
import { MonthListEnum } from 'app/back-office/shared/models/common/chart-gen.models';
import { cloneDeep, isEqual, isMatch, union, uniqBy } from 'lodash';
import { parseZone } from 'moment';
import { CurrentOS } from '../enums/current-os.enum';
import { weekDayEnum } from '../enums/week-day.enum';
import { BootstrapDate } from '../models/bootstrap-date.model';
import { ActiveChannelDTO, ChannelDTO } from '../models/channel.model';
import { OrderItem } from '../models/order-item.model';
import { OrderValidationResponseModel } from '../models/order-validation.model';
import { Shop } from '../models/shop.model';
import { OrderTypeEnum } from '../enums/order-type.enum.model';
import { UserCultureEnum } from '../enums/user-culture.enum.model';
import { BulkTaskStatusEnum } from 'app/back-office/shared/enum/bulk-task-status.enum';
import { UserInfo } from '../models/user-info.model';
import { OrderItemsColumnsEnum } from 'app/back-office/shared/enum/order-columns.enum.model';
import { ColumnsList, ColumnsListGroup, ColumnsListGroupEnum } from 'app/back-office/components/common/order-detail/data/columns-order-items-grid';
import { OrderProductListVM } from 'app/back-office/shared/models/back-office-admin-common/order-product-list.model';
import { environment } from 'environments/environment';
import { OrderDeliveryDate } from 'app/back-office/shared/models/back-office-admin-common/order.model';
import { FormGroup } from '@angular/forms';
import { VariantDTO } from 'app/back-office/shared/models/products/variant.models';
import { GenderTypeEnum } from 'app/back-office/shared/enum/gender-type.enum.model';
import { SizesMapper } from 'app/shop/shared/utilities/sizes-mapper';
import { LocationFullDTO } from '../models/location.model';
import { RolesEnum } from '../enums/roles.enum';
import { TenantTypeEnum } from 'environments/tenant-type.enum';
import { CoreMenuItem } from 'app/back-office/shared/models/core';
import { DatePipe } from '@angular/common';
import { orderColumnsEnum, OrderColumnsList, OrderColumnsListItem } from 'app/back-office/components/common/order-grid/data/columns-order-grid';
import { crColumnsEnum, CrColumnsList, CrColumnsListItem } from 'app/back-office/components/customization-requests/data/columns-customization-requests-grid';
import { TypeOrdersListEnum } from 'app/back-office/shared/enum/type-orders-list.enum';
import { CustomerFieldsConfig, CustomerFullDTO, GeneralFieldsDTO, GeneralFieldsVM } from 'app/back-office/shared/models/customer/customer.model';
import { ContactVM } from 'app/back-office/shared/models/back-office-admin-common/contact.models';
import { TenantSettingType, TenantSettingsDTO } from '../models/tenant.model';
import { OrderLabelTypeEnum } from 'app/back-office/shared/models/back-office-admin-common/order-label-type.enum';

export class UtilityFunctions {

  // public static formatItalianVATNumber(vatNumber: string): string {
  //   // Rimuove spazi e rende uniforme
  //   const trimmed = vatNumber?.trim().replace(/\s+/g, '') || '';

  //   // Controlla che abbia almeno due caratteri
  //   if (trimmed.length < 2) return trimmed;

  //   // Estrae i primi due caratteri
  //   const prefix = trimmed.slice(0, 2);

  //   // Verifica che siano lettere
  //   const isLetters = /^[A-Za-z]{2}$/.test(prefix);

  //   if (!isLetters) {
  //     // Se i primi due non sono lettere, restituisco l'input invariato
  //     return trimmed;
  //   }

  //   // Caso 1: se è "IT", rimuovo le due lettere
  //   if (prefix.toUpperCase() === "IT") {
  //     return trimmed.slice(2);
  //   }

  //   // Caso 2: se è diverso da "IT", aggiungo uno spazio dopo le prime due lettere
  //   return `${prefix.toUpperCase()} ${trimmed.slice(2)}`;
  // }

  public static formatVATNumber(tenantType: TenantTypeEnum, vatPrefix: string, vatNumber: string): string {
    const result = `${(vatPrefix || '').toUpperCase()}${(vatNumber || '')}`
    const trimmed = result?.trim().replace(/\s+/g, '') || '';
    if (trimmed.length < 2) return trimmed;
    const prefix = trimmed.slice(0, 2);
    const isLetters = /^[A-Za-z]{2}$/.test(prefix);
    if (!isLetters) {
      return trimmed;
    }
    if (tenantType === TenantTypeEnum.Italy) {
      return prefix.toUpperCase() === "IT" ? trimmed.slice(2) : `${prefix.toUpperCase()} ${trimmed.slice(2)}`;
    }
    return trimmed;
  }

  public static getArrayKeyValuesFromEnum<T extends object>(enumType: T, exceptions?: number[]): { id: number, value: string }[] {
    return Object.keys(enumType)
      .filter(key => isNaN(Number(key)))
      .map(key => {
        const formattedKey = key
          .replace(/_/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .toUpperCase();

        return {
          id: (enumType as any)[key],
          value: formattedKey
        };
      }).filter(item => !exceptions?.length || !exceptions.some(e => e === item.id));
  }

  public static generalFieldsToView(items: GeneralFieldsDTO[]): GeneralFieldsVM[] {
    return items.map(item => {
      const fullname = item.description && item.code !== item.description ? `${item.code} - ${item.description}` : item.code;
      return new GeneralFieldsVM({ ...item, fullname });
    });
  }

  public static locationsAreDifferent(newLocation: LocationFullDTO, cachedLocation: LocationFullDTO) {
    if (!newLocation || !cachedLocation) return false;
    return newLocation.address1 !== cachedLocation.address1 ||
      newLocation.city !== cachedLocation.city ||
      newLocation.zipCode !== cachedLocation.zipCode ||
      newLocation.province !== cachedLocation.province ||
      newLocation.country?.id !== cachedLocation.country?.id ||
      newLocation.placeId !== cachedLocation.placeId;
  }

  /**
   * Creates a deep copy of the target object using lodash cloneDeep.
   * @template T The type of the object to clone
   * @param target The object to create a deep copy of
   * @returns A deep copy of the target object
   * @example
   * const original = { user: { name: 'John', address: { city: 'NYC' } } };
   * const copy = UtilityFunctions.DeepClone(original);
   * copy.user.name = 'Jane'; // original.user.name remains 'John'
   */
  public static DeepClone<T>(target: T): T {
    // target = cloneDeep(target);
    // source = cloneDeep(source);
    // if (target && source) Object.assign(target, source);
    return cloneDeep(target);
  }

  /**
   * Creates a deep copy of the target object and optionally merges properties from a source object.
   * @template T The type of the target object
   * @param target The object to create a deep copy of
   * @param source Optional partial object containing properties to merge into the cloned target
   * @returns A deep copy of the target object with optional source properties merged
   * @example
   * const original = { user: { name: 'John', age: 30 }, active: true };
   * const updates = { active: false };
   * const result = UtilityFunctions.DeepCloneAndMerge(original, updates);
   * // result: { user: { name: 'John', age: 30 }, active: false }
   * // original remains unchanged
   */
  public static DeepCloneAndMerge<T>(target: T, source?: Partial<T>): T {
    const result = cloneDeep(target);
    if (source) Object.assign(result, source);
    return result;
  }

  public static newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    }).toUpperCase();
  }

  public static isMatch<T>(obj1: T, obj2: T): boolean {
    return isMatch(obj1, obj2);
  }

  public static isEquals<T>(obj1: T, obj2: T): boolean {
    return isEqual(obj1, obj2);
  }

  public static transformCrDate(_datePipe: DatePipe, date: string): string {
    return _datePipe.transform(date, 'd MMM y HH:mm');
  }

  public static isArraysEquals<T>(array1: T[], array2: T[], valueProperty?: string): boolean {
    if (array1 && array2) {
      if (valueProperty) {
        array1.sort((a, b) => {
          if (a[valueProperty] < b[valueProperty]) { return -1; }
          if (a[valueProperty] > b[valueProperty]) { return 1; }
          return 0;
        });
        array2.sort((a, b) => {
          if (a[valueProperty] < b[valueProperty]) { return -1; }
          if (a[valueProperty] > b[valueProperty]) { return 1; }
          return 0;
        });
      } else {
        array1.sort((a, b) => {
          if (a < b) { return -1; }
          if (a > b) { return 1; }
          return 0;
        });
        array2.sort((a, b) => {
          if (a < b) { return -1; }
          if (a > b) { return 1; }
          return 0;
        });
      }
    }
    return isEqual(array1, array2);
  }

  public static fromBootstrapDateToString(date: BootstrapDate): string {
    const dat = new Date(date.year, date.month - 1, date.day);
    return parseZone(dat).utc(true).toISOString();
  }

  public static fromDateStringTODateISOString(date: string): string {
    return parseZone(new Date(date).setHours(0, 0, 0, 0)).utc(true).toISOString();
  }

  public static fromDateToDateISOString(date: Date): string {
    return parseZone(date.setHours(0, 0, 0, 0)).utc(true).toISOString();
  }

  public static fromDateToDateISOStringKeepingHours(date: Date, disableUTC?: boolean): string {
    return parseZone(date).utc(!disableUTC).toISOString();
  }

  public static fromDateStringTODate(date: string): Date {
    return parseZone(new Date(date)).utc(true).toDate();
  }

  public static spliByUpperCase(myStr: string): string {
    let result = '';
    myStr?.match(/[A-Z][a-z]+/g).forEach((str, i) => {
      if (!!i) result += ' ';
      result += str;
    });
    return result;
  }

  public static getRequestResolution(resolutions: { [key: number]: string; }, requestResolution: number, placeHolderText?: string): string {
    let currentMin = null;
    let result = null;
    if (!!resolutions) {
      let keys = Object.keys(resolutions);
      if (keys.some(x => +x >= requestResolution)) keys = keys.filter(x => +x >= requestResolution);
      keys.forEach(x => {
        if (currentMin == null || Math.abs(+x - requestResolution) < currentMin) {
          currentMin = Math.abs(+x - requestResolution);
          result = resolutions[x];
        }
      });
    }
    return this.fixImagePath(result, requestResolution, placeHolderText);
  }

  public static fixImagePath(src: string, dim?: number, placeHolderText?: string): string {
    if (!dim) dim = 100;
    let result = 'https://placehold.co/' + dim;
    if (!!placeHolderText) result += '?text=' + placeHolderText;
    if (!!src) result = src;
    if (result.indexOf('http') != 0) result = 'https://' + result;
    return result;
  }

  public static isReOrderActive(shop: Shop, currentUser?: UserInfo): boolean {
    return (shop.activatedStoreChannels.some(sc => !sc.channel.supportsPreOrderOnShiftedComputedDeliveryDate)) && ((currentUser && this.IsAdminOrStoreOwner(currentUser)) || (!!shop && (!shop.reOrderActiveFrom || new Date(shop.reOrderActiveFrom) < new Date()) && (!shop.reOrderActiveTo || new Date(shop.reOrderActiveTo) > new Date())));
  }

  public static buildParams(...params: [string, any][]): Record<string, any> {
    const options = {};
    for (const [key, value] of params) {
      options[key] = value;
    }
    return UtilityFunctions.returnParams(options);
  }

  public static returnParams(options: Record<string, any>): Record<string, any> {
    const params = {};
    for (const key in options) {
      const value = options[key];
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    }
    return params;
  }

  public static isPreOrderActive(shop: Shop, currentUser?: UserInfo): boolean {
    return (currentUser && this.IsAdminOrStoreOwner(currentUser)) || (!!shop && (!shop.preOrderActiveFrom || new Date(shop.preOrderActiveFrom) < new Date()) && (!shop.preOrderActiveTo || new Date(shop.preOrderActiveTo) > new Date()));
  }

  public static IsAdminOrStoreOwner(currentUser: UserInfo) {
    return currentUser?.mainRole <= RolesEnum.StoreUser;
  }

  public static IsDesigner(currentUser: UserInfo) {
    return currentUser?.mainRole === RolesEnum.Designer;
  }

  // true se l'utente è un cliente in una qualsiasi delle varianti (Customer, CustomerOwner, CustomerGuest)
  public static IsCustomer(currentUser: UserInfo): boolean {
    const role = currentUser?.mainRole;
    return role === RolesEnum.Customer
      || role === RolesEnum.CustomerOwner
      || role === RolesEnum.CustomerGuest;
  }

  // true se il tenant dell'utente è il tenant italiano
  public static IsItalianTenant(currentUser: UserInfo): boolean {
    return currentUser?.tenant?.id?.toLowerCase() === environment.tenants.find(x => x.tenantType === TenantTypeEnum.Italy)?.id?.toLowerCase();
  }

  // true se l'utente fa parte di un'agenzia (AgencyOwner o Agent)
  public static IsAgency(currentUser: UserInfo): boolean {
    const role = currentUser?.mainRole;
    return role === RolesEnum.AgencyOwner
      || role === RolesEnum.Agent;
  }

  // true se l'utente è "interno" al negozio (Administrator, StoreOwner, StoreUser, Designer)
  public static IsInternalUser(currentUser: UserInfo): boolean {
    const role = currentUser?.mainRole;
    return role === RolesEnum.Administrator
      || role === RolesEnum.StoreOwner
      || role === RolesEnum.StoreUser
      || role === RolesEnum.Designer;
  }

  public static goToTargetBlank(router: Router, route: string, queryParams?: Params): void {
    const url = router.serializeUrl(
      router.createUrlTree([route], { queryParams })
    );
    window.open(url, '_blank');
  }
  public static openUrlInNewTab(url: string): void {
    const win = window.open(url, '_blank');
    win.focus();
  }

  public static getReOrderAvailableDate(computedDeliveryDateDaysDelta: number, dateSelected?: Date): string {
    const date = new Date();
    date.setDate(((dateSelected ? dateSelected : new Date()).getDate() + computedDeliveryDateDaysDelta));
    switch (date.getDay()) {
      case weekDayEnum.Saturday:
        date.setDate(date.getDate() + 2);
        break;
      case weekDayEnum.Sunday:
        date.setDate(date.getDate() + 1);
        break;
    }
    const myDate = new Date(date.setHours(0, 0, 0, 0));
    const result = parseZone(myDate).utc(true).toISOString();
    return result;
  }

  public static getCurrentShopByUrl(shops: Shop[]): Shop {
    if (window.location.hostname.indexOf('localhost') >= 0) {
      const lastSelectedShopId = +localStorage.getItem('shopId');
      return lastSelectedShopId ? (shops?.find(x => x.id === lastSelectedShopId) || (shops || [])[0]) : (shops || [])[0];
    } else {
      const shop = shops?.find(x => x.uri?.indexOf(window.location.origin) >= 0);
      return shop;
    }
  }

  public static RemoveDuplicateData<T>(arg: T[]): T[] {
    return union(arg);
  }

  public static checkOrderValidationToOpenModal(orderItems: OrderItem[], orderValidation: OrderValidationResponseModel, orderType: OrderTypeEnum, channel: ChannelDTO, computedDeliveryDateDaysDelta: number): boolean {
    return !!orderValidation?.minimumQuantityValidationByProductPalette?.length
      || !!orderValidation?.stockInfoForPointOfSale?.stockLimitExceeded
      || !!orderValidation?.stockInfoByProductAndPalette?.some(x => x.stockLimitExceeded)
      || !!orderItems?.some(x => this.checkOrderItemAvailability(x, orderValidation, orderType, channel, computedDeliveryDateDaysDelta));
  }

  public static checkOrderItemAvailability(orderItem: OrderItem, orderValidation: OrderValidationResponseModel, orderType: OrderTypeEnum, channel: ChannelDTO, computedDeliveryDateDaysDelta: number): boolean {
    const val = orderValidation?.orderItemsValidation?.find(ov => ov.orderItemId === orderItem.id);
    return !!val &&
      ((new Date(orderItem.requestedDeliveryDate).setHours(0, 0, 0, 0) < new Date(this.getReOrderAvailableDate(computedDeliveryDateDaysDelta)).setHours(0, 0, 0, 0)
        && (orderType === OrderTypeEnum.ReOrder || channel.supportsPreOrderOnShiftedComputedDeliveryDate)) ||
        this.defineAvailableQty(val.availableQuantity, orderItem.variant.quantityMinimumPerOrder, channel, orderType) === 0);
  }

  public static returnChannelNameForRoot(channelName: string): string {
    return channelName?.replace(/\s/g, '')?.toLowerCase();
  }

  public static capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  public static manipulateCompanyName(name: string, companyForms: ICompanyForm[]): { manipulatedName: string, selectedForm: ICompanyForm; } {
    const splits = (name || '').split(' ');
    const form = splits.length ? splits[splits.length - 1] : null;
    const selectedForm = form ? companyForms?.find(x => x.name.toLowerCase().replace('.', '') === form.toLowerCase().replace('.', '')) : null;
    if (selectedForm) {
      splits.pop();
      name = splits.reduce((sum, x) => sum + x + ' ', '').trim();
    }
    return { manipulatedName: name, selectedForm };
  }

  public static NumberFromatting(value: number, currency?: 'EUR'): string {
    if (currency) {
      return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(value);
    } else {
      return new Intl.NumberFormat('it-IT').format(value);
    }
  }

  public static getValueForcedQuantityIncrement(valueToCheck: number, qtyBuyIncrement: number, residualQty: number): number {
    const valInt = Math.round(valueToCheck) > residualQty ? residualQty : Math.round(valueToCheck);
    if (qtyBuyIncrement && valInt && valInt % qtyBuyIncrement !== 0) {
      const qtyMulti = valInt - (valInt % qtyBuyIncrement) + qtyBuyIncrement;
      return qtyMulti > residualQty ? qtyMulti - qtyBuyIncrement : qtyMulti;
    } else {
      return valInt;
    }
  }

  public static defineAvailableQty(availableQuantity: number, quantityMinimumPerOrder: number, channel: ChannelDTO, orderType: OrderTypeEnum) {
    const result = ((channel.supportsMinimumVariantQuantityForReOrder && orderType == OrderTypeEnum.ReOrder) || (channel.supportsMinimumVariantQuantityForPreOrder && orderType == OrderTypeEnum.PreOrder))
      && availableQuantity < quantityMinimumPerOrder ?
      0 : availableQuantity?.toString() ? availableQuantity : -1;
    return result;
  }

  public static getDashboardMonthList(): { month: number, name: string, year: number; }[] {
    const pastYearMonthsQuantity = 2;
    const result: { month: number, name: string, year: number; }[] = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const monthNumber = Object.values(MonthListEnum).filter(value => typeof value !== 'string') as number[];
    const monthsPast2Year = [];
    if (currentMonth < pastYearMonthsQuantity) {
      for (let i = 12 - pastYearMonthsQuantity + 2; i <= 12; i++) {
        monthsPast2Year.push(i);
      }
    }
    const monthsPastYear = monthNumber.filter(m => m > currentMonth - pastYearMonthsQuantity);
    const monthsCurrentYear = monthNumber.filter(m => m <= currentMonth);
    monthsPast2Year.forEach(m => {
      result.push({ year: currentYear - 2, month: m, name: `${Object.values(MonthListEnum).filter(value => typeof value === 'string')[m - 1]} ${(new Date().getFullYear() - 2).toString().slice(2, 4)}` });
    });
    monthsPastYear.forEach(m => {
      result.push({ year: currentYear - 1, month: m, name: `${Object.values(MonthListEnum).filter(value => typeof value === 'string')[m - 1]} ${(new Date().getFullYear() - 1).toString().slice(2, 4)}` });
    });
    monthsCurrentYear.forEach(m => {
      result.push({ year: currentYear, month: m, name: `${Object.values(MonthListEnum).filter(value => typeof value === 'string')[m - 1]} ${new Date().getFullYear().toString().slice(2, 4)}` });
    });
    return result;
  }

  public static isEmailValid(email: string): boolean {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  }

  /** Toglie ogni spaziatura (anche tab e spazi unificatori incollati da Excel/Outlook): per i campi che non ammettono spazi, es. email e telefoni. */
  public static removeAllSpaces(value: string): string {
    return typeof value === 'string' ? value.replace(/\s/g, '') : value;
  }

  /** Telefono/cellulare/fax: solo cifre, almeno 5. Niente prefisso `+` e niente separatori (scelta 2026-09-18). Il valore NON va ripulito prima, o le lettere passerebbero. */
  public static isPhoneValid(phone: string): boolean {
    return /^[0-9]{5,}$/.test(phone ?? '');
  }

  public static downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.click();
    window.URL.revokeObjectURL(url);
    link.remove();
  }

  public static downloadBase64(base64: string, name: string) {
    if (!base64 || !base64.startsWith('data:')) {
      base64 = `data:${base64}`;
    }

    const link = document.createElement('a');
    link.href = base64;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public static arrayUniq<T>(array: T[]): T[] {
    return array = union(array);
  }

  public static arrayUniqBy<T>(array: T[], by: string): T[] {
    return array = uniqBy(array, by);
  }

  public static arraySortBy<T>(array: T[], by: string): T[] {
    return array.sort((a, b) => {
      if (a[by] < b[by]) { return -1; }
      if (a[by] > b[by]) { return 1; }
      return 0;
    });
  }

  public static returnUrlPartFromOrderType(orderType: OrderTypeEnum) {
    return orderType === OrderTypeEnum.PreOrder ? 'pre-order' : 're-order';
  }

  public static returnLanguageFromCulture(culture: UserCultureEnum): 'en' | 'es' | 'it' | 'fr' {
    switch (culture) {
      case UserCultureEnum.en: return 'en';
      case UserCultureEnum.it: return 'it';
      case UserCultureEnum.es: return 'es';
      case UserCultureEnum.fr: return 'fr';
    }
  }

  public static removeEmptyProperties<T>(obj: T): T {
    Object.entries(obj).forEach(([key, value]) => {
      // check if the value is an object
      if (value && typeof value === 'object') {
        // recursively call the function
        this.removeEmptyProperties(value);
      } else if (value === undefined || value === null || value === '') {
        delete obj[key];
      }
    });
    return obj;
  }

  public static spainOrderLabels(): OrderLabelTypeEnum[] {
    return [
      OrderLabelTypeEnum.B2BSHOP_DWEB, OrderLabelTypeEnum.B2BSHOP_HEROES, OrderLabelTypeEnum.B2BSHOP_KOI, OrderLabelTypeEnum.B2BSHOP_KSIHQ,
      OrderLabelTypeEnum.B2BSHOP_MARIN, OrderLabelTypeEnum.B2BSHOP_REAL, OrderLabelTypeEnum.B2BSHOP_RIAZOR, OrderLabelTypeEnum.B2BSHOP_VALLSUR, OrderLabelTypeEnum.B2BSHOP_ZORRILLA,
      OrderLabelTypeEnum.B2BSHOP_UNIPREUS, OrderLabelTypeEnum.B2BSHOP_CADWEB, OrderLabelTypeEnum.B2BSHOP_CADBAH, OrderLabelTypeEnum.B2BSHOP_CADMIR,
      OrderLabelTypeEnum.B2BSHOP_CADCEN
      // TODO: Da riattivare quando riattiviamo orderlabels relative
      // OrderLabelTypeEnum.WH_DWEB, OrderLabelTypeEnum.WH_HEROES, OrderLabelTypeEnum.WH_KOI, OrderLabelTypeEnum.WH_KSIHQ, OrderLabelTypeEnum.WH_MARIN, OrderLabelTypeEnum.WH_REAL,
      // OrderLabelTypeEnum.WH_RIAZOR, OrderLabelTypeEnum.WH_VALLSUR, OrderLabelTypeEnum.WH_ZORRILLA
    ];
  }

  public static franceOrderLabels(): OrderLabelTypeEnum[] {
    return [
      OrderLabelTypeEnum.WH_DWEB, OrderLabelTypeEnum.WH_HEROES, OrderLabelTypeEnum.WH_KOI, OrderLabelTypeEnum.WH_KSIHQ,
      OrderLabelTypeEnum.WH_MARIN, OrderLabelTypeEnum.WH_REAL, OrderLabelTypeEnum.WH_RIAZOR, OrderLabelTypeEnum.WH_VALLSUR, OrderLabelTypeEnum.WH_ZORRILLA,
    ];
  }

  public static orderLabelsByTenant(tenantId: string, data: { id: OrderLabelTypeEnum, value: string }[]): { id: OrderLabelTypeEnum, value: string }[] {
    const tenantType = this.getEnvironmentTenant(tenantId)?.tenantType;
    if (tenantType !== TenantTypeEnum.KappaSpain) {
      data = data.filter(el => !this.spainOrderLabels().some(x => x === el.id));
    }
    if (tenantType !== TenantTypeEnum.KappaFrance && tenantType !== TenantTypeEnum.KwayFrance) {
      data = data.filter(el => !this.franceOrderLabels().some(x => x === el.id));
    }
    return data;
  }


  public static determineLabelOfStatusOperation(status: BulkTaskStatusEnum) {
    let result = null;
    switch (status) {
      case BulkTaskStatusEnum.AwaitingProcessing:
        result = 'STATUS.AWAITING_PROCESSING';
        break;
      case BulkTaskStatusEnum.Processing:
        result = 'STATUS.PROCESSING';
        break;
      case BulkTaskStatusEnum.ProcessedWithIssues:
        result = 'STATUS.PROCESSED_WITH_ISSUES';
        break;
      case BulkTaskStatusEnum.ProcessedWithoutIssues:
        result = 'STATUS.PROCESSED';
        break;
      case BulkTaskStatusEnum.ProcessingFailure:
        result = 'STATUS.PROCESSING_FAILURE';
        break;
      default:
        result = 'Status not found';
        break;
    }
    return result;
  }

  public static superAdminEmails(): string[] {
    return [
      'nestola@basic.net',
      'radano@basic.net',
      'chiara.soddu@orbyta.it',
      'trionfo@basic.net',
      'sanfilippo@kappa.it',
      'luca.ciranni@orbyta.it',
      'sergio.pugliese@orbyta.it',
      'samuele.sicura@orbyta.it',
      'federicapelle@basic.net'
    ];
  }

  public static getChannelsByChannelProperty(channel: ActiveChannelDTO[], channelProperty: string): string {
    let result: string[] = [];
    channel.forEach(element => {
      if (element[channelProperty]) {
        result.push(element.channel.name);
      }
    });
    return result.join(', ');
  }

  public static orderItemsListColumnsGroups(isAdmin: boolean, setImageColumnHidden?: boolean): ColumnsListGroup[] {
    let columnsList = ColumnsList;
    const selectedColumnsIds = this.getOrderItemsColumnsFromLocalStorage();
    if (selectedColumnsIds?.length) columnsList.forEach(x => x.checked = selectedColumnsIds.some(id => id === x.id));
    if (setImageColumnHidden) {
      const imgCol = columnsList.find(c => c.id === OrderItemsColumnsEnum.variantImg);
      if (imgCol) imgCol.checked = false;
    }
    if (!isAdmin) columnsList = columnsList.filter(ci => !ci.hideToAgentAndCustomer);
    return Object.keys(ColumnsListGroupEnum).filter(k => typeof (ColumnsListGroupEnum[k]) === 'number').map((key) => new ColumnsListGroup({
      id: ColumnsListGroupEnum[key],
      title: 'ORDER_DETAIL.COLUMNS.GROUP_' + key,
      items: columnsList.filter(x => x.groupId === ColumnsListGroupEnum[key]),
      hideInExportXlsx: this.orderItemsColumnsIdsNotXlsxExportable()?.some(id => id === ColumnsListGroupEnum[key])
    }));
  }

  // Bump this version every time a new column is added to OrderColumnsList.
  // This invalidates all users' saved column selections and resets to defaults.
  private static readonly ORDER_GRID_COLUMNS_VERSION = '2';

  public static orderGridListColumns(tenantSettings: TenantSettingsDTO[], typeOrdersList?: TypeOrdersListEnum): OrderColumnsListItem[] {
    let columnsList = this.DeepClone(OrderColumnsList);

    if (!tenantSettings?.some(x => x.type === TenantSettingType.EnabledOrdesBulkOperations && !!x.value)) {
      columnsList = columnsList.filter(x => x.id !== orderColumnsEnum.bulk && x.id !== orderColumnsEnum.operations)
    }

    if (typeOrdersList === TypeOrdersListEnum.carts) {
      columnsList = columnsList.filter(col => !col.hideToCart);
    } else {
      columnsList = columnsList.filter(col => !col.hideToOrder);
    }

    this.migrateOrderGridColumnsVersion(typeOrdersList);

    const selectedColumnsIds = this.getOrderGridColumnsFromLocalStorage(typeOrdersList);
    if (selectedColumnsIds?.length) {
      columnsList.forEach(x => x.isVisible = selectedColumnsIds.some(id => id === x.id));
    }
    return columnsList;
  }

  public static setOrderGridColumnsInLocalStorage(orderGridColumnsIds: orderColumnsEnum[], typeOrdersList?: TypeOrdersListEnum) {
    const key = typeOrdersList === TypeOrdersListEnum.carts ? 'cart-grid-columns-ids' : 'order-grid-columns-ids';
    const columnsIdsString = JSON.stringify(orderGridColumnsIds);
    localStorage.setItem(key, columnsIdsString);
  }

  private static getOrderGridColumnsFromLocalStorage(typeOrdersList?: TypeOrdersListEnum): orderColumnsEnum[] {
    const key = typeOrdersList === TypeOrdersListEnum.carts ? 'cart-grid-columns-ids' : 'order-grid-columns-ids';
    const result: orderColumnsEnum[] = JSON.parse(localStorage.getItem(key) || '[]') || [];
    return result;
  }

  private static migrateOrderGridColumnsVersion(typeOrdersList?: TypeOrdersListEnum): void {
    const versionKey = typeOrdersList === TypeOrdersListEnum.carts ? 'cart-grid-columns-version' : 'order-grid-columns-version';
    const columnsKey = typeOrdersList === TypeOrdersListEnum.carts ? 'cart-grid-columns-ids' : 'order-grid-columns-ids';
    if (localStorage.getItem(versionKey) !== this.ORDER_GRID_COLUMNS_VERSION) {
      localStorage.removeItem(columnsKey);
      localStorage.setItem(versionKey, this.ORDER_GRID_COLUMNS_VERSION);
    }
  }

  private static readonly CR_GRID_COLUMNS_VERSION = '1';

  /** Colonne della lista CR: default dal data file, visibilità ripristinata da localStorage (vedi order-grid) */
  public static customizationRequestsGridListColumns(isCustomerGuest: boolean): CrColumnsListItem[] {
    let columnsList: CrColumnsListItem[] = this.DeepClone(CrColumnsList);

    if (isCustomerGuest) {
      columnsList = columnsList.filter(col => !col.hideToCustomerGuest);
    }

    this.migrateCrGridColumnsVersion();

    const selectedColumnsIds = this.getCrGridColumnsFromLocalStorage();
    if (selectedColumnsIds?.length) {
      columnsList.forEach(x => x.isVisible = selectedColumnsIds.some(id => id === x.id));
    }
    return columnsList;
  }

  public static setCrGridColumnsInLocalStorage(crGridColumnsIds: crColumnsEnum[]) {
    localStorage.setItem('customization-requests-grid-columns-ids', JSON.stringify(crGridColumnsIds));
  }

  private static getCrGridColumnsFromLocalStorage(): crColumnsEnum[] {
    return JSON.parse(localStorage.getItem('customization-requests-grid-columns-ids') || '[]') || [];
  }

  private static migrateCrGridColumnsVersion(): void {
    if (localStorage.getItem('customization-requests-grid-columns-version') !== this.CR_GRID_COLUMNS_VERSION) {
      localStorage.removeItem('customization-requests-grid-columns-ids');
      localStorage.setItem('customization-requests-grid-columns-version', this.CR_GRID_COLUMNS_VERSION);
    }
  }

  public static setOrderItemsColumnsInLocalStorage(orderItemsColumnsIds: OrderItemsColumnsEnum[], checkAndResetImgColumn?: boolean) {
    if (checkAndResetImgColumn) {
      const currentSelections: OrderItemsColumnsEnum[] = JSON.parse(localStorage.getItem('order-items-columns-ids') || '[]') || [];
      if (currentSelections.some(id => id === OrderItemsColumnsEnum.variantImg) && !orderItemsColumnsIds.some(id => id === OrderItemsColumnsEnum.variantImg))
        orderItemsColumnsIds.push(OrderItemsColumnsEnum.variantImg);
      else if (!currentSelections.some(id => id === OrderItemsColumnsEnum.variantImg) && orderItemsColumnsIds.some(id => id === OrderItemsColumnsEnum.variantImg))
        orderItemsColumnsIds = orderItemsColumnsIds.filter(id => id !== OrderItemsColumnsEnum.variantImg);
    }
    const columnsIdsString = JSON.stringify(orderItemsColumnsIds);
    localStorage.setItem('order-items-columns-ids', columnsIdsString);
  }

  public static checkCustomDeliveryDates(maxDate: string, requestedDeliveryDates: string[]) {
    const minDate = this.elaborateMinDateInCustomDeliveryDate(requestedDeliveryDates);
    return !maxDate || !requestedDeliveryDates.length || (!!minDate && new Date(maxDate).getTime() >= new Date(minDate).getTime());
  }

  public static elaborateMinDateInCustomDeliveryDate(requestedDeliveryDates: string[]): string {
    let orderItemsMaxDate: string = null;
    requestedDeliveryDates.forEach(date => {
      if (orderItemsMaxDate === null || new Date(orderItemsMaxDate) < new Date(date))
        orderItemsMaxDate = date;
    });
    return orderItemsMaxDate;
  }

  public static elaborateOrderItemsByDates(orderItems: OrderItem[], orderProductList: OrderProductListVM[]): OrderProductListVM[] {
    const productMap = new Map<number, OrderProductListVM>(orderProductList.map(p => [p.orderItemId, p]));
    const resultMap = new Map<string, OrderProductListVM>();
    const result: OrderProductListVM[] = [];

    const processItem = (source: OrderProductListVM, qty: number, date: string) => {
      const item: OrderProductListVM = { ...source, realQuantity: qty, orderItemDeliveryDate: date };
      const key = `${item.productId}_${date}`;
      let existingProd = resultMap.get(key);
      if (!existingProd) {
        item.hasCustomDeliveryDate = !!(item.requestedDeliveryDate && item.orderItemDeliveryDate !== item.requestedDeliveryDate);
        existingProd = {
          ...item,
          children: {
            dataset: [],
            dataToView: [],
            filteredData: [],
            checkedAll: false,
            filteredLength: null,
            loaderId: UtilityFunctions.newGuid()
          }
        };
        resultMap.set(key, existingProd);
        result.push(existingProd);
      } else {
        existingProd.realQuantity += qty;
      }
      existingProd.children.dataset.push(item);
    };

    orderItems.forEach(orderItem => {
      const source = productMap.get(orderItem.id);
      if (!source) {
        return;
      }
      if (orderItem.deliveries?.length) {
        orderItem.deliveries.forEach(cd => {
          const qty = cd.erpQuantity ? cd.erpQuantity - cd.erpCancelledQuantity : cd.quantity;
          const date = cd.erpDeliveryDate || cd.customDeliveryDate || orderItem.requestedDeliveryDate;
          processItem(source, qty, date);
        });
      } else {
        processItem(source, source.realQuantity, source.orderItemDeliveryDate);
      }
    });

    result.forEach(prod => {
      prod.children.dataset.sort((a, b) => {
        if (a.colorCode > b.colorCode) return 1;
        if (a.colorCode < b.colorCode) return -1;
        return a.sizeOrder - b.sizeOrder;
      });
      prod.children.filteredData = [...prod.children.dataset];
    });

    return result.sort((a, b) => new Date(a.orderItemDeliveryDate).getTime() - new Date(b.orderItemDeliveryDate).getTime());
  }

  public static getEnvironmentTenant(tenantId: string) {
    return environment.tenants?.find(t => t.id?.toLowerCase() === tenantId?.toLowerCase());
  }

  public static getEnvironmentTenantByType(tenantType: TenantTypeEnum) {
    return environment.tenants?.find(t => t.tenantType === tenantType);
  }

  // Restituisce true se il tenant corrisponde a una delle varianti francesi (Kappa o Kway)
  public static isFranceTenant(tenantId: string): boolean {
    const tenantType = this.getEnvironmentTenant(tenantId)?.tenantType;
    return tenantType === TenantTypeEnum.KappaFrance || tenantType === TenantTypeEnum.KwayFrance;
  }

  // Segmento di rotta verso il dettaglio dell'ordine emesso da una Customization Request:
  // i tenant francesi lo raggiungono sotto 'orders', tutti gli altri (Italia, Spagna, ...) sotto 'carts'.
  // Le due rotte montano lo stesso componente app-order-detail: cambia il backTitle, quindi la lista
  // a cui riporta il back button. Non include i '../': la profondità la compone il chiamante.
  public static crOrderDetailRouteSegment(tenantId: string): string {
    return this.isFranceTenant(tenantId) ? 'orders' : 'carts';
  }

  public static deliveryDates(elaboratedOrderItems: { data: OrderProductListVM[], orderItemDeliveryDate: string, hasCustomDeliveryDate: boolean, quantity: number; }[]): OrderDeliveryDate[] {
    let result: OrderDeliveryDate[] = [];
    elaboratedOrderItems.forEach(element => {
      let existing = result.find(x => x.deliveryDate === element.orderItemDeliveryDate);
      if (!existing) {
        result.push(new OrderDeliveryDate({
          data: element.data,
          count: element.quantity,
          deliveryDate: element.orderItemDeliveryDate,
          isCustom: element.hasCustomDeliveryDate
        }));
      } else {
        existing.data = existing.data.concat(element.data);
        existing.count += element.quantity;
        existing.isCustom = existing.isCustom || element.hasCustomDeliveryDate;
      }
    });
    result.sort((a, b) => { return new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime(); });
    return result;
  }

  private static elaborateOrderItemByDate(result: OrderProductListVM[], myItem: OrderProductListVM) {
    let existingProd = result.find(x => x.productId === myItem.productId && x.orderItemDeliveryDate === myItem.orderItemDeliveryDate);
    if (!existingProd) {
      myItem.hasCustomDeliveryDate = myItem.requestedDeliveryDate && myItem.orderItemDeliveryDate != myItem.requestedDeliveryDate;
      existingProd = UtilityFunctions.DeepClone(myItem);
      existingProd.children = {
        dataset: [],
        dataToView: [],
        filteredData: [],
        checkedAll: false,
        filteredLength: null,
        loaderId: UtilityFunctions.newGuid()
      };
      result.push(existingProd);
    } else {
      existingProd.realQuantity += myItem.realQuantity;
    }
    existingProd.children.dataset.push(myItem);
    existingProd.children.dataset = existingProd.children.dataset.sort((a, b) => {
      if (a.colorCode > b.colorCode) return 1;
      if (a.colorCode < b.colorCode) return -1;
      return a.sizeOrder - b.sizeOrder;
    });
    existingProd.children.filteredData = [...existingProd.children.dataset];
  }

  public static elaborateOrderItemsByDatesThreeLevels(orderItems: OrderItem[], orderProductList: OrderProductListVM[]): OrderProductListVM[] {
    const result: OrderProductListVM[] = [];

    const dateGroups = new Map<string, OrderProductListVM[]>();

    orderItems.forEach(item => {
      const myItem = UtilityFunctions.DeepClone(orderProductList.find(x => x.orderItemId == item.id));
      if (item.deliveries?.length) {
        item.deliveries.forEach(cd => {
          myItem.realQuantity = cd.erpQuantity ? cd.erpQuantity - cd.erpCancelledQuantity : cd.quantity;
          myItem.orderItemDeliveryDate = cd.erpDeliveryDate || cd.customDeliveryDate || item.requestedDeliveryDate;

          const dateKey = myItem.orderItemDeliveryDate;
          if (!dateGroups.has(dateKey)) {
            dateGroups.set(dateKey, []);
          }
          dateGroups.get(dateKey).push(UtilityFunctions.DeepClone(myItem));
        });
      } else {
        const dateKey = myItem.orderItemDeliveryDate || item.requestedDeliveryDate;
        myItem.orderItemDeliveryDate = dateKey;
        if (!dateGroups.has(dateKey)) {
          dateGroups.set(dateKey, []);
        }
        dateGroups.get(dateKey).push(myItem);
      }
    });

    Array.from(dateGroups.entries()).sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime()).forEach(([date, items]) => {
      const dateItem = UtilityFunctions.DeepClone(items[0]);

      dateItem.productDetails = date; //Imposta data come titolo
      dateItem.productCode = '';
      dateItem.colorCode = '';
      dateItem.colorName = '';
      dateItem.size = '';
      dateItem.sku = '';
      dateItem.ean = '';
      dateItem.quantity = items.reduce((sum, item) => sum + item.realQuantity, 0);
      dateItem.realQuantity = dateItem.quantity;
      dateItem.totalRetail = items.reduce((sum, item) => sum + item.totalRetail, 0);
      dateItem.totalWholesale = items.reduce((sum, item) => sum + item.totalWholesale, 0);
      dateItem.discountedTotalPrice = items.reduce((sum, item) => sum + item.discountedTotalPrice, 0);
      dateItem.totalDiscountPercentage = dateItem.totalWholesale > 0 ? +((100 * (dateItem.totalWholesale - dateItem.discountedTotalPrice) / dateItem.totalWholesale).toFixed(2)) : 0;

      dateItem.children = {
        dataset: [],
        dataToView: [],
        filteredLength: null,
        loaderId: UtilityFunctions.newGuid()
      };

      const productGroups = new Map<number, OrderProductListVM[]>();
      items.forEach(item => {
        if (!productGroups.has(item.productId)) {
          productGroups.set(item.productId, []);
        }
        productGroups.get(item.productId).push(item);
      });

      productGroups.forEach((productItems, productId) => {
        const productItem = UtilityFunctions.DeepClone(productItems[0]);

        productItem.quantity = productItems.reduce((sum, item) => sum + item.realQuantity, 0);
        productItem.realQuantity = productItem.quantity;
        productItem.totalRetail = productItems.reduce((sum, item) => sum + item.totalRetail, 0);
        productItem.totalWholesale = productItems.reduce((sum, item) => sum + item.totalWholesale, 0);
        productItem.discountedTotalPrice = productItems.reduce((sum, item) => sum + item.discountedTotalPrice, 0);
        productItem.totalDiscountPercentage = productItem.totalWholesale > 0 ? +((100 * (productItem.totalWholesale - productItem.discountedTotalPrice) / productItem.totalWholesale).toFixed(2)) : 0;

        productItem.colorCode = '';
        productItem.colorName = '';
        productItem.size = '';
        productItem.sku = '';
        productItem.ean = '';

        productItem.children = {
          dataset: productItems.sort((a, b) => {
            if (a.colorCode > b.colorCode) return 1;
            if (a.colorCode < b.colorCode) return -1;
            return a.sizeOrder - b.sizeOrder;
          }),
          dataToView: [],
          filteredLength: null,
          loaderId: UtilityFunctions.newGuid()
        };

        dateItem.children.dataset.push(productItem);
      });

      result.push(dateItem);
    });

    return result;
  }


  private static getOrderItemsColumnsFromLocalStorage(): OrderItemsColumnsEnum[] {
    const result: OrderItemsColumnsEnum[] = JSON.parse(localStorage.getItem('order-items-columns-ids') || '[]') || [];
    return result;
  }

  private static orderItemsColumnsIdsNotXlsxExportable(): ColumnsListGroupEnum[] {
    return [ColumnsListGroupEnum.FULFILLMENT];
  }

  public static calculateDiscountPercentage(totalNetPrice: number, originalTotalPrice: number): number {
    if (originalTotalPrice === 0) {
      throw new Error("Original price cannot be zero.");
    }

    const discount = ((originalTotalPrice - totalNetPrice) / originalTotalPrice) * 100;
    return Math.round(discount * 100) / 100; // arrotonda a 2 cifre decimali
  }

  /**
   * Arrotonda a 2 decimali preservando null/undefined (che devono restare "nessun valore", non diventare 0).
   * Serve a display: per le righe salvate a prezzo target il BE ricava la percentuale dagli snapshot
   * e la restituisce a piena precisione (es. 34.5455).
   */
  public static roundToTwoDecimals(value: number): number {
    return value === null || value === undefined ? value : Math.round(value * 100) / 100;
  }

  public static clearAllValidators(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.clearValidators();
      control.updateValueAndValidity();
      if (control instanceof FormGroup) {
        this.clearAllValidators(control);
      }
    });
  }
  public static updateAllValueAndValidity(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.updateValueAndValidity();
    });
  }
  public static getFilenameFromContentDisposition(contentDisposition: string): string {
    const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = regex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      return matches[1].replace(/['"]/g, '');
    }
    return '';
  }

  public static convertSizesName(variants: VariantDTO[], gender: GenderTypeEnum, styleGroupId: number): VariantDTO[] {
    if (gender !== undefined && gender !== null && !!styleGroupId) {
      (variants || []).forEach(v => {
        const sizeItemMapper = new SizesMapper().sizesItems.find(x => x.gender == gender && x.styleGroupId == styleGroupId && x.sizes.some(s => s.sizeName == v.size?.name?.toUpperCase()));
        if (sizeItemMapper) {
          const size = sizeItemMapper.sizes.find(s => s.sizeName == v.size?.name?.toUpperCase());
          v.size.name = size?.newSizeName;
        }
      });
    }
    return variants;
  }

  public static findFirstMenuItemByRole(items: CoreMenuItem[], mainRole: RolesEnum, tmpResult: string = ''): string {
    let result = null;
    let el = items.find(x => x.url && (x.onlyRoles?.some(r => r === mainRole) || (!x.onlyRoles?.length && !x.children)));
    if (el) result = tmpResult + '/' + el.url;
    else {
      el = items.find(y => !y.onlyRoles?.length && y.children?.length);
      if (el) result = this.findFirstMenuItemByRole(el.children, mainRole, tmpResult + (el.url ? '/' + el.url : ''));
    }
    return result;
  }

  public static checkMenuItemByRole(items: CoreMenuItem[], mainRole: RolesEnum, currentUrlPartValue: string): boolean {
    let result = items.some(x => x.id?.toLowerCase() === currentUrlPartValue?.toLowerCase() && (x.onlyRoles?.some(r => r === mainRole) || !x.onlyRoles?.length));
    if (!result) {
      const filtered = items.filter(y => (y.onlyRoles?.some(r => r === mainRole) || !y.onlyRoles?.length) && y.children?.length);
      for (let i = 0; i < filtered.length; i++) {
        result = this.checkMenuItemByRole(filtered[i].children, mainRole, currentUrlPartValue);
        if (result) break;
      }
    }
    return result;
  }

  public static redirectToUnauthorized(router: Router) {
    router.navigateByUrl('errors/unauthorized');
  }

  public static removeNullsFromObj(obj) {
    Object.keys(obj).forEach(key => !obj[key] && delete obj[key]);
    return obj;
  }

  public static initLocation(): LocationFullDTO {
    return {
      id: null,
      address1: '',
      address2: '',
      city: '',
      province: '',
      country: {name: '', iso2: '', iso3: '', id: null},
      zipCode: '',
      externalId: '',
      placeId: '',
      longitude: null,
      latitude: null,
      customerLocations: []
    }
  }

  public static initCustomer(tenantSettings: CustomerFieldsConfig): CustomerFullDTO {
    return {
      billingLocationStoreCustomerLocations: [],
      routingNumber: '',
      externalId: '',
      name: '',
      companyForm: {
        id: null,
        name: '',
        externalId: '',
        multiSelectCheck: false
      },
      companyFormId: null,
      knownAs: '',
      fiscalCode: '',
      iban: '',
      sirenNumber: '',
      glnNumber: '',
      customerLocations: [
        {
          location: {
            id: null,
            address1: '',
            address2: '',
            city: '',
            province: '',
            country: {id: null, name: '', iso2: '', iso3: ''},
            zipCode: '',
            externalId: '',
            placeId: '',
            longitude: null,
            latitude: null
          },
          locationType: null,
          contacts: tenantSettings.contactTypeRequired.map(contact => ({
            type: contact,
            value: ''
          })) as ContactVM[]
        }
      ],
      abi: '',
      acceptsOrders: false,
      agency: '',
      availableAllowance: '',
      bankAccountId: '',
      bankAccountNumber: '',
      bankName: '',
      basicCardId: '',
      blockOrderType: null,
      cab: '',
      centralPaymentNumber: '',
      checkInMessage: '',
      checkInStatus: null,
      commercialAllowance: 0,
      creationTime: '',
      creditLimit: 0,
      creatorId: null,
      currency: '',
      currencyType: null,
      customerCategories: [],
      customerType: null,
      customerVouchers: [],
      id: null,
      isActiveEnterprise: false,
      isCentralPaymentAccount: false,
      isCompliantActivity: false,
      isCorrectRegistry: false,
      isFactor: false,
      isInvoicingAccount: false,
      lastModificationTime: '',
      lastModifierId: null,
      onlineStore: '',
      orders: '',
      ordersBlockedByUser: '',
      packageGroupModeType: null,
      paCode: '',
      paymentTerm: {
        id: null,
        name: '',
        externalId: '',
        order: null
      },
      paymentTermId: null,
      paymentTermOverrides: [],
      pecEmail: '',
      pointsOfSale: [],
      sdiCode: '',
      swift: '',
      tier: '',
      totalPurchased: '',
      vatNumber: '',
      invoicingParentCustomerId: null,
      centralPaymentParentCustomerId: null,
      tenantSettings: tenantSettings
    };
  }

  /**
   * Scrive `newObject` in localStorage solo se diverso dal valore già presente.
   * Ritorna true se ha scritto. (Spostato da CoreService — vedi ADR 002.)
   */
  public static updateLocalStorageIfChanged(key: string, newObject: any): boolean {
    const storedData = localStorage.getItem(key);
    const newObjectString = JSON.stringify(newObject);
    if (storedData === null || storedData !== newObjectString) {
      localStorage.setItem(key, newObjectString);
      return true;
    }
    return false;
  }

  /** Pulizia parametri query: scarta undefined/null e valori non serializzabili. (Spostato da CoreService — vedi ADR 002.) */
  public static prepareParams(params: any): any {
    const output: any = {};
    if (!params) {
      return output;
    }
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null &&
        (typeof params[key] === 'string' || !isNaN(params[key]) || params[key]?.length > 0)) {
        output[key] = params[key];
      }
    });
    return output;
  }

  /** Rilevamento OS/mobile dallo user agent. (Spostato da CoreService — vedi ADR 002.) */
  public static detectCurrentOS(): { os: CurrentOS, isMobile: boolean } {
    const userAgent = window.navigator.userAgent,
      platform = (window as any).navigator?.userAgentData?.platform || window.navigator.platform,
      macosPlatforms = ['macOS', 'Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
      windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
      iosPlatforms = ['iPhone', 'iPad', 'iPod'];
    let os: CurrentOS = CurrentOS.Unknown;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (macosPlatforms.indexOf(platform) !== -1) {
      os = CurrentOS.MacOS;
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      os = CurrentOS.iOS;
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      os = CurrentOS.Windows;
    } else if (/Android/.test(userAgent)) {
      os = CurrentOS.Android;
    } else if (/Linux/.test(platform)) {
      os = CurrentOS.Linux;
    }

    return { os, isMobile };
  }

  /** Tastiera dei campi quantita e prezzo: solo cifre e tasti di editing; con `canUseDecimals` anche `.` e `,`. */
  public static blockSpecialChars(event: KeyboardEvent, canUseDecimals?: boolean): void {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
    if (canUseDecimals) allowedKeys.push('.', ',');
    const isNumberKey = event.key >= '0' && event.key <= '9';
    if (!isNumberKey && !allowedKeys.includes(event.key)) event.preventDefault();
  }
}
